import { Box, Button, Text, useToken } from "@chakra-ui/react"
import {
  AnimatePresence,
  domAnimation,
  LazyMotion,
  m,
  useReducedMotion,
} from "framer-motion"
import React from "react"

import { formatNumber } from "@/utils/formatNumber"
import { HeartFillIcon, HeartOutlineIcon } from "@/components/icons"

const PARTICLES = 14
const BASE_RADIUS = 42
const SPARKLE_DURATION = 0.7

const sparkleParticleStyle: React.CSSProperties = {
  left: "50%",
  pointerEvents: "none",
  position: "absolute",
  top: "50%",
  translate: "-50% -50%",
}

const sparkleRingStyle: React.CSSProperties = {
  borderRadius: "50%",
  height: "2.2em",
  left: "50%",
  position: "absolute",
  top: "50%",
  translate: "-50% -50%",
  width: "2.2em",
}

const heartIconStyle: React.CSSProperties = {
  alignItems: "center",
  bottom: 0,
  display: "flex",
  fontSize: "1.8em",
  justifyContent: "center",
  left: 0,
  position: "absolute",
  right: 0,
  top: 0,
}

const getParticleSeed = (index: number) => {
  const value = Math.sin(index * 12.9898) * 43758.5453

  return value - Math.floor(value)
}

/**
 * Sparkles: a tiny particle + ring burst that fires when `key` changes.
 */
const Sparkles = ({ color = "#E53E3E", disabled = false }) => {
  const prefersReducedMotion = useReducedMotion()
  const isOff = disabled || prefersReducedMotion

  if (isOff) return null

  const items = Array.from({ length: PARTICLES }).map((_, i) => {
    const angle = (i / PARTICLES) * Math.PI * 2
    const seed = getParticleSeed(i + 1)
    const radius = BASE_RADIUS * (0.75 + seed * 0.5)
    const x = Math.cos(angle) * radius
    const y = Math.sin(angle) * radius
    const delay = getParticleSeed(i + 11) * 0.02

    return (
      <m.div
        animate={{ opacity: 0, scale: 0.1, x, y }}
        initial={{
          opacity: 1,
          rotate: getParticleSeed(i + 23) * 360,
          scale: 0.6,
          x: 0,
          y: 0,
        }}
        key={i}
        style={sparkleParticleStyle}
        transition={{ delay, duration: SPARKLE_DURATION, ease: "easeOut" }}
      >
        {/* sparkle dot */}
        <Box
          bg={color}
          borderRadius="full"
          boxShadow={`0 0 0 1px ${color} inset, 0 0 10px ${color}`}
          h="6px"
          w="6px"
        />
      </m.div>
    )
  })

  return (
    <LazyMotion features={domAnimation}>
      <Box
        bottom={0}
        left={0}
        pointerEvents="none"
        position="absolute"
        right={0}
        top={0}
      >
        {/* expanding ring */}
        <m.div
          animate={{ opacity: 0, scale: 1.6 }}
          initial={{ opacity: 0.6, scale: 0.2 }}
          style={{
            ...sparkleRingStyle,
            border: `2px solid ${color}`,
          }}
          transition={{ duration: 0.6, ease: "easeOut" }}
        />
        {items}
      </Box>
    </LazyMotion>
  )
}

interface LikeButtonProps {
  likeCount?: string
  handleLike?: () => void
  isLiked?: boolean
  loading?: boolean
  disabled?: boolean
}

const LikeButton = ({
  disabled = false,
  handleLike,
  isLiked = false,
  likeCount = "0",
  loading = false,
}: LikeButtonProps) => {
  const [burstKey, setBurstKey] = React.useState(0)
  const prevIsLikedRef = React.useRef(isLiked)
  const prefersReducedMotion = useReducedMotion()
  const [red500, gray500] = useToken("colors", ["red.500", "currentColor"])

  // Trigger burst when isLiked changes from false to true
  React.useEffect(() => {
    if (isLiked && !prevIsLikedRef.current) {
      setBurstKey((prev) => prev + 1)
    }
    prevIsLikedRef.current = isLiked
  }, [isLiked])

  return (
    <LazyMotion features={domAnimation}>
      <Box asChild display="flex" position="relative">
        <m.div className="like-btn" whileTap={{ scale: 0.93 }}>
          <Button
            _active={{ transform: "translateY(0px)" }}
            _hover={{ color: "red", transform: "translateY(-1px)" }}
            aria-label={isLiked ? "Unlike" : "Like"}
            colorPalette={isLiked ? "red" : "gray"}
            disabled={disabled}
            gap="6px"
            h="full"
            loading={loading}
            minHeight={"64px"}
            onClick={handleLike}
            px={3}
            py={1}
            rounded={"0"}
            size="lg"
            variant="ghost"
          >
            <Box h="1.8em" position="relative" w="1.8em">
              <AnimatePresence initial={false} mode="wait">
                {!isLiked ? (
                  <m.div
                    animate={{ opacity: 1, rotate: 0, scale: 1 }}
                    exit={{ opacity: 0, rotate: 10, scale: 0.4 }}
                    initial={{ opacity: 0, rotate: -10, scale: 0.8 }}
                    key="outline"
                    style={{
                      ...heartIconStyle,
                      color: gray500,
                    }}
                    transition={{ damping: 30, stiffness: 500, type: "spring" }}
                  >
                    <HeartOutlineIcon fill={"currentColor"} />
                  </m.div>
                ) : (
                  <m.div
                    animate={{ opacity: 1, rotate: 0, scale: 1.06 }}
                    exit={{ opacity: 0, scale: 0.5 }}
                    initial={{ opacity: 0, rotate: -10, scale: 0.3 }}
                    key="fill"
                    style={{
                      ...heartIconStyle,
                      color: red500,
                    }}
                    transition={{ damping: 22, stiffness: 600, type: "spring" }}
                  >
                    <HeartFillIcon />
                  </m.div>
                )}
              </AnimatePresence>
            </Box>
            <Text>{formatNumber(likeCount)}</Text>
          </Button>

          {/* Sparkles overlay; re-mounts on each like via key */}
          {isLiked && (
            <Sparkles
              color={red500}
              disabled={!!prefersReducedMotion}
              key={burstKey}
            />
          )}
        </m.div>
      </Box>
    </LazyMotion>
  )
}

export default LikeButton
